import React from 'react';
import * as Tabs from '@radix-ui/react-tabs';
import classNames from 'classnames';

import { createStyle, createStyles } from '../../../utils/createStyle';
import { colors } from '../../../styles/settings';

const styles = createStyles({
    tab: createStyle(
        'relative font-bold text-center cursor-pointer text-dark-blue bg-pale-blue-50',
        {
            border: '1px solid transparent',
            '&:hover': { color: 'black' },

            '&[data-state="active"]': {
                color: colors.brandDark,
                border: `1px solid ${colors.brand}`,
                borderBottom: 'none',
                marginBottom: '-1px',
                background: colors.white,
            },
        }
    ),
    tabOfferContent: createStyle('rounded-t', {
        '&[data-state="active"]': {
            border: `1px solid ${colors.bonusPink}`,
            borderBottom: 'none',
        },
    }),
    tabOffer: createStyle(
        'absolute left-0 right-0 top-0 text-base lg:text-sm bg-green min-w-full rounded-t',
        {
            color: 'white',
            height: 3,
        }
    ),
    tabTitle: createStyle(
        'relative p-2 sm:px-4 text-sm sm:text-base flex items-center justify-center'
    ),
    tabPill: createStyle(
        'flex relative px-2 font-bold text-center cursor-pointer rounded-3xl border border-solid text-grey-1',
        {
            marginRight: '10px',
            color: colors.brandDark,

            '&[data-state="active"]': {
                color: colors.brand,
                borderWidth: '2px',
            },
            '&[data-state="active"] > div': {
                paddingBottom: '7px',
                paddingTop: '7px',
            },
        }
    ),
});

export interface TabProps {
    offer?: string;
    title: string;
    icon?: React.ReactNode;
    tabId: string;
    selected: boolean;
    className?: string;
    noUnderline?: boolean;
    noUpperBorder?: boolean;
}

export const Tab = ({
    offer,
    title,
    icon,
    tabId,
    selected,
    className,
    noUnderline = false,
    noUpperBorder = false,
}: TabProps) => {
    return (
        <Tabs.Tab
            className={classNames(
                styles.tab,
                className,
                offer ? styles.tabOfferContent : 'rounded-t'
            )}
            value={tabId}
        >
            <div
                className={classNames(
                    'group',
                    styles.tabTitle,
                    'px-4',
                    selected ? 'text-brand' : 'text-brand hover:text-brand'
                )}
            >
                {!noUpperBorder || selected ? (
                    <div
                        className={classNames(
                            styles.tabOffer,
                            offer ? 'bg-bonus-pink' : 'bg-brand'
                        )}
                    />
                ) : null}
                {icon}
                <span
                    className={`${
                        !noUnderline ? 'underline whitespace-pre-wrap' : 'whitespace-pre-wrap'
                    } ${!selected ? 'group-hover:underline' : ''}`}
                >
                    {title}
                </span>
            </div>
        </Tabs.Tab>
    );
};

export const TabPill = ({ offer, title, icon, tabId, selected, className }: TabProps) => {
    return (
        <Tabs.Tab
            className={classNames(
                styles.tabPill,
                !selected && !offer && 'border-grey-light hover:border-brand',
                !offer
                    ? 'border-brand'
                    : selected
                    ? 'border-bonus-pink'
                    : 'border-lighter-pink hover:border-bonus-pink',
                className
            )}
            value={tabId}
        >
            <div
                className={classNames(
                    styles.tabTitle,
                    'px-1',
                    !selected ? 'text-brand-dark hover:text-brand' : 'text-brand'
                )}
            >
                {icon}
                <span className="whitespace-pre-wrap">{title}</span>
            </div>
        </Tabs.Tab>
    );
};
